import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner, ErrorMessage, EmptyState } from './CommonUI';
import { useAuth } from './AuthContext';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import AdSenseAd from './AdSenseAd';

const Blog = () => {
    const { user, checkAdminStatus } = useAuth();
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalPosts: 0,
        postsPerPage: 10
    });
    const [loadedImages, setLoadedImages] = useState({});
    const [isAdmin, setIsAdmin] = useState(false);

    useDocumentTitle(
        'Blog',
        'MyBrickLog blog: LEGO® news, collecting guides, market value updates, and practical tips for managing your LEGO collection.',
        { canonicalUrl: 'https://www.mybricklog.com/blog' }
    );

    const category = searchParams.get('category');
    const page = parseInt(searchParams.get('page')) || 1;

    useEffect(() => {
        fetchBlogData();
    }, [category, page]);

    // Non-blocking admin check
    useEffect(() => {
        const checkAdmin = async () => {
            if (user) {
                try {
                    const adminStatus = await checkAdminStatus();
                    setIsAdmin(adminStatus);
                } catch (error) {
                    console.error('Error checking admin status:', error);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        };
        
        checkAdmin();
    }, [user, checkAdminStatus]);

    const fetchBlogData = async () => {
        setIsLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (category) params.append('category', category);
            if (page > 1) params.append('page', page.toString());
            
            // Fetch posts and categories in parallel
            const [postsResponse, categoriesResponse] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/blog_get_posts.php?${params}`),
                fetch(`${process.env.REACT_APP_API_URL}/blog_get_categories.php`)
            ]);

            if (!postsResponse.ok) {
                throw new Error('Failed to fetch posts');
            }
            if (!categoriesResponse.ok) {
                throw new Error('Failed to fetch categories');
            }

            const postsData = await postsResponse.json();
            const categoriesData = await categoriesResponse.json();

            if (!postsData.success) {
                throw new Error(postsData.message || 'Failed to load posts');
            }
            if (!categoriesData.success) {
                throw new Error(categoriesData.message || 'Failed to load categories');
            }

            setPosts(postsData.posts);
            setCategories(categoriesData.categories);
            setPagination({
                currentPage: postsData.pagination.current_page,
                totalPages: postsData.pagination.total_pages,
                totalPosts: postsData.pagination.total_posts,
                postsPerPage: postsData.pagination.posts_per_page
            });
        } catch (error) {
            console.error('Error fetching blog data:', error);
            setError('Unable to load blog posts. Please try again later.');
        }
        setIsLoading(false);
    };

    const handleImageLoad = (postId) => {
        setLoadedImages(prev => ({ ...prev, [postId]: true }));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatReadTime = (minutes) => {
        const readTime = Math.max(1, parseInt(minutes, 10) || 1);
        return `${readTime} min read`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <LoadingSpinner text="Loading blog posts..." size="lg" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <ErrorMessage message={error} onRetry={() => fetchBlogData()} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-gradient-to-r from-red-600 to-red-700 text-white">
                <div className="absolute inset-0 pointer-events-none">
                    <img
                        src="/images/2x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-8 left-8 w-24 h-24 opacity-20 transform rotate-12 select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-red.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-10 right-12 w-14 h-14 opacity-40 transform -rotate-12 select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/2x1-brick-red.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-4 left-[18%] w-20 h-20 opacity-35 transform -rotate-6 select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-10 right-1/4 w-12 h-12 opacity-30 transform rotate-12 select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/2x1-brick-yellow.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-1/2 right-[14%] w-16 h-16 opacity-35 transform rotate-6 select-none object-contain"
                        draggable="false"
                    />
                    <img
                        src="/images/1x1-brick-red.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-8 right-8 w-10 h-10 opacity-45 transform -rotate-12 select-none object-contain"
                        draggable="false"
                    />
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
                    <div className="text-center relative">
                        <h1 className="mb-4 flex justify-center px-2">
                            <img
                                src="/images/MyBrickBlog.webp"
                                alt="MyBrickLog Blog"
                                className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg h-auto object-contain"
                            />
                        </h1>
                        <p className="text-xl text-red-100 max-w-3xl mx-auto mb-6">
                            Stay up to date with the latest LEGO news, reviews, and collection tips from our community.
                        </p>
                        
                        {/* Admin New Post Button */}
                        {isAdmin && (
                            <div className="mt-6">
                                <Link
                                    to="/admin/blog/new"
                                    className="inline-flex items-center px-6 py-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 backdrop-blur-sm border border-white border-opacity-30"
                                >
                                    <FontAwesomeIcon icon="plus" className="mr-2" />
                                    Create New Post
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="lg:grid lg:grid-cols-4 lg:gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-3">
                        {/* Category Filter */}
                        {category && (
                            <div className="mb-6">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Posts in "{categories.find(c => c.slug === category)?.name || category}"
                                    </h2>
                                    <Link 
                                        to="/blog" 
                                        className="text-red-600 hover:text-red-800 font-medium"
                                    >
                                        <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                                        All Posts
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Posts Grid */}
                        {posts.length === 0 ? (
                            <EmptyState 
                                message="No blog posts found."
                                actionText="View All Posts"
                                onAction={() => window.location.href = '/blog'}
                                icon="newspaper"
                            />
                        ) : (
                            <div className="space-y-6">
                                {posts.map((post) => (
                                    <article
                                        key={post.id}
                                        className="bg-white border border-gray-100 rounded shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 lg:flex"
                                    >
                                        {/* Featured Image */}
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            className="block w-full lg:w-72 xl:w-80 flex-shrink-0 bg-gray-100 overflow-hidden"
                                            tabIndex={-1}
                                            aria-hidden="true"
                                        >
                                            <div className="aspect-video lg:aspect-auto lg:h-full relative">
                                                {!loadedImages[post.id] && (
                                                    <Skeleton height="100%" style={{ position: 'absolute', inset: 0 }} />
                                                )}
                                                <img
                                                    src={post.featured_image || '/images/lego_piece_questionmark.png'}
                                                    alt={post.title}
                                                    className={`w-full h-full object-cover transition-transform duration-300 hover:scale-105 ${loadedImages[post.id] ? 'opacity-100' : 'opacity-0'}`}
                                                    onLoad={() => handleImageLoad(post.id)}
                                                    onError={(e) => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                                                />
                                            </div>
                                        </Link>

                                        {/* Content */}
                                        <div className="p-5 sm:p-6 flex flex-col justify-between flex-1 min-w-0">
                                            <div>
                                                {/* Category + Date */}
                                                <div className="flex items-center gap-3 mb-3">
                                                    <Link
                                                        to={`/blog?category=${post.category.slug}`}
                                                        className="blog-category-badge-sm"
                                                    >
                                                        {post.category.name}
                                                    </Link>
                                                    <span className="text-xs text-gray-400">
                                                        {formatDate(post.published_at)}
                                                    </span>
                                                </div>

                                                {/* Title */}
                                                <h2 className="mb-3">
                                                    <Link
                                                        to={`/blog/${post.slug}`}
                                                        className="blog-card-title"
                                                    >
                                                        {post.title}
                                                    </Link>
                                                </h2>

                                                {/* Excerpt */}
                                                {post.excerpt && (
                                                    <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 mb-4">
                                                        {post.excerpt}
                                                    </p>
                                                )}

                                                {/* Tags */}
                                                {post.tags && post.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5 mb-4">
                                                        {post.tags.map(tag => (
                                                            <span key={tag.id} className="blog-tag">
                                                                {tag.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer meta */}
                                            <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-xs text-gray-400">
                                                <div className="flex items-center gap-3">
                                                    <span>
                                                        <FontAwesomeIcon icon="user" className="mr-1" />
                                                        {post.author.username}
                                                    </span>
                                                    <span>
                                                        <FontAwesomeIcon icon="eye" className="mr-1" />
                                                        {post.view_count}
                                                    </span>
                                                    <span>
                                                        <FontAwesomeIcon icon="clock" className="mr-1" />
                                                        {formatReadTime(post.read_time_minutes)}
                                                    </span>
                                                </div>
                                                <Link
                                                    to={`/blog/${post.slug}`}
                                                    className="text-xs font-bold uppercase tracking-wide text-red-600 hover:text-red-800 transition-colors"
                                                >
                                                    Read More
                                                    <FontAwesomeIcon icon="arrow-right" className="ml-1.5" />
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="mt-8 flex justify-center">
                                <nav className="flex items-center gap-2">
                                    {pagination.currentPage > 1 && (
                                        <Link
                                            to={`/blog${category ? `?category=${category}` : ''}${pagination.currentPage > 2 ? `${category ? '&' : '?'}page=${pagination.currentPage - 1}` : ''}`}
                                            className="px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        >
                                            <FontAwesomeIcon icon="chevron-left" />
                                        </Link>
                                    )}
                                    
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(pageNum => (
                                        <Link
                                            key={pageNum}
                                            to={`/blog${category ? `?category=${category}` : ''}${pageNum > 1 ? `${category ? '&' : '?'}page=${pageNum}` : ''}`}
                                            className={`px-3 py-2 rounded-md ${
                                                pageNum === pagination.currentPage
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum}
                                        </Link>
                                    ))}
                                    
                                    {pagination.currentPage < pagination.totalPages && (
                                        <Link
                                            to={`/blog${category ? `?category=${category}` : ''}?page=${pagination.currentPage + 1}`}
                                            className="px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        >
                                            <FontAwesomeIcon icon="chevron-right" />
                                        </Link>
                                    )}
                                </nav>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="mt-8 lg:mt-0 space-y-5">
                        {/* Categories */}
                        <div className="bg-white border border-gray-100 rounded shadow-sm p-5">
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                                Categories
                            </p>
                            <ul className="space-y-0.5">
                                <li>
                                    <Link
                                        to="/blog"
                                        className={`flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                                            !category
                                                ? 'bg-red-50 text-red-700 font-semibold'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                    >
                                        <span>All Posts</span>
                                        {!category && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                                    </Link>
                                </li>
                                {categories.map(cat => (
                                    <li key={cat.id}>
                                        <Link
                                            to={`/blog?category=${cat.slug}`}
                                            className={`flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                                                category === cat.slug
                                                    ? 'bg-red-50 text-red-700 font-semibold'
                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                            }`}
                                        >
                                            <span>{cat.name}</span>
                                            <span className="text-xs text-gray-400">{cat.count}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Recent Posts */}
                        <div className="bg-white border border-gray-100 rounded shadow-sm p-5">
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                                Recent Posts
                            </p>
                            <div className="space-y-4">
                                {posts.slice(0, 4).map(post => (
                                    <Link
                                        key={post.id}
                                        to={`/blog/${post.slug}`}
                                        className="flex gap-3 group"
                                    >
                                        <div className="w-14 h-14 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                                            <img
                                                src={post.featured_image || '/images/lego_piece_questionmark.png'}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug">
                                                {post.title}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {formatDate(post.published_at)}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar ad */}
                        <AdSenseAd type="in-article" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Blog;
